import React from 'react';
import { Table, Button, Space, Tag, Popconfirm, message, Input, Grid, Card } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deleteBDE, updateBDE } from '../store/bdeSlice';
import { updateOrder } from '../store/ordersSlice';
import { addPayout } from '../store/payoutsSlice';
import { EditOutlined, DeleteOutlined, CheckOutlined, FileExcelOutlined } from '@ant-design/icons';
import { exportToExcel } from '../utils/exportUtils';
import { formatCurrency } from '../utils/formatters';

const BDEList = () => {
  const { users } = useSelector((state) => state.bde);
  const { orders } = useSelector((state) => state.orders);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');

  const isEligibleForCommission = (status) => {
    return ['Expected', 'Processing', 'Completed'].includes(status);
  };

  const handlePay = async (bdeId) => {
    const bdeOrders = orders.filter(o => o.bdeId === bdeId && !o.commissionPaid && isEligibleForCommission(o.status));
    if (bdeOrders.length === 0) {
      message.info('No pending commission to pay.');
      return;
    }

    const totalAmount = bdeOrders.reduce((sum, o) => sum + (Number(o.amount) * 0.10 || 0), 0);

    if (totalAmount <= 0) {
      message.warning('Total commission amount is 0. Nothing to pay.');
      return;
    }

    try {
      // 1. Add Payout Entry
      await dispatch(addPayout({
        bdeId,
        amount: totalAmount,
        transactionId: `BULK-PAY-${Date.now()}`,
        notes: `Bulk payment for ${bdeOrders.length} orders`
      })).unwrap();

      // 2. Mark Orders as Paid
      await Promise.all(bdeOrders.map(order =>
        dispatch(updateOrder({ ...order, commissionPaid: true })).unwrap()
      ));

      message.success(`Paid commission for ${bdeOrders.length} orders`);
    } catch (err) {
      console.error('Payment error:', err);
      message.error('Failed to pay commission: ' + (err.message || err));
    }
  };

  const handleDelete = (id) => {
    dispatch(deleteBDE(id))
      .unwrap()
      .then(() => message.success('User deleted'))
      .catch((err) => message.error('Failed to delete user: ' + err));
  };

  const handleApprove = (user) => {
    dispatch(updateBDE({ ...user, status: 'verified' }))
      .unwrap()
      .then(() => message.success('User approved'))
      .catch((err) => message.error('Failed to approve user: ' + err));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => String(a.id).localeCompare(String(b.id)),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => String(a.name || '').localeCompare(String(b.name || '')),
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
      sorter: (a, b) => String(a.mobile || '').localeCompare(String(b.mobile || '')),
    },
    {
      title: 'Account No.',
      dataIndex: 'accountNumber',
      key: 'accountNumber',
      render: (text) => <span style={{ fontFamily: 'monospace' }}>{text || '-'}</span>
    },
    {
      title: 'Pincode',
      dataIndex: 'pincode',
      key: 'pincode',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => String(a.status || '').localeCompare(String(b.status || '')),
      render: (status) => (
        <Tag color={status === 'verified' ? 'green' : 'orange'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Total Comm.',
      key: 'totalCommission',
      render: (_, record) => {
        const total = orders
          .filter(o => o.bdeId === record.id && isEligibleForCommission(o.status))
          .reduce((sum, o) => sum + (Number(o.amount) * 0.10 || 0), 0);
        return formatCurrency(total);
      }
    },
    {
      title: 'Paid',
      key: 'paidCommission',
      render: (_, record) => {
        const paid = orders
          .filter(o => o.bdeId === record.id && o.commissionPaid && isEligibleForCommission(o.status))
          .reduce((sum, o) => sum + (Number(o.amount) * 0.10 || 0), 0);
        return <span style={{ color: 'green' }}>{formatCurrency(paid)}</span>;
      }
    },
    {
      title: 'Remaining',
      key: 'remainingCommission',
      render: (_, record) => {
        const remaining = orders
          .filter(o => o.bdeId === record.id && !o.commissionPaid && isEligibleForCommission(o.status))
          .reduce((sum, o) => sum + (Number(o.amount) * 0.10 || 0), 0);
        return <span style={{ color: 'red', fontWeight: 'bold' }}>{formatCurrency(remaining)}</span>;
      }
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right',
      render: (_, record) => {
        const remaining = orders
          .filter(o => o.bdeId === record.id && !o.commissionPaid && isEligibleForCommission(o.status))
          .reduce((sum, o) => sum + (Number(o.amount) * 0.10 || 0), 0);

        return (
          <Space size="middle">
            <Button
              type="primary"
              size="small"
              icon={<span style={{ marginRight: 4 }}>₹</span>}
              onClick={() => handlePay(record.id)}
              disabled={remaining === 0}
            >
              Pay
            </Button>
            {record.status === 'pending' && (
              <Button
                type="link"
                icon={<CheckOutlined />}
                onClick={() => handleApprove(record)}
              >
                Approve
              </Button>
            )}
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/admin/bde-users/edit/${record.id}`)}
            >
              Edit
            </Button>
            <Popconfirm
              title={remaining > 0 ? "Cannot delete user" : "Delete the user"}
              description={remaining > 0 ? "Please pay remaining commission first." : "Are you sure to delete this user?"}
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
              disabled={remaining > 0}
            >
              <Button type="link" danger icon={<DeleteOutlined />} disabled={remaining > 0}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        )
      },
    },
  ];

  const screens = Grid.useBreakpoint();

  const filteredUsers = users.filter((u) => {
    const q = query.toLowerCase().trim();
    if (!q) return true;
    const id = String(u.id || '').toLowerCase();
    const name = String(u.name || '').toLowerCase();
    const mobile = String(u.mobile || '').toLowerCase();
    const status = String(u.status || '').toLowerCase();
    return id.includes(q) || name.includes(q) || mobile.includes(q) || status.includes(q);
  });

  const handleExport = () => {
    if (!users || users.length === 0) {
      message.warning('No users to export');
      return;
    }

    const dataToExport = users.map(user => {
      // Calculate commissions for export to match view
      const total = orders
        .filter(o => o.bdeId === user.id && isEligibleForCommission(o.status))
        .reduce((sum, o) => sum + (Number(o.amount) * 0.10 || 0), 0);

      const paid = orders
        .filter(o => o.bdeId === user.id && o.commissionPaid && isEligibleForCommission(o.status))
        .reduce((sum, o) => sum + (Number(o.amount) * 0.10 || 0), 0);

      const remaining = orders
        .filter(o => o.bdeId === user.id && !o.commissionPaid && isEligibleForCommission(o.status))
        .reduce((sum, o) => sum + (Number(o.amount) * 0.10 || 0), 0);

      return {
        ID: user.id,
        Name: user.name,
        Mobile: user.mobile,
        'Account Number': user.accountNumber || '-',
        Pincode: user.pincode,
        Status: user.status,
        'Total Commission': total,
        'Paid Commission': paid,
        'Remaining Commission': remaining,
        'Created At': user.createdAt
      };
    });

    exportToExcel(dataToExport, 'BDE_Users_List');
    message.success('BDE Users exported successfully');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 16 }}>
        <h2>BDE Users</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Input.Search
            allowClear
            placeholder="Search users"
            style={{ width: 320, maxWidth: '100%' }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button icon={<FileExcelOutlined />} onClick={handleExport}>
            Export to Excel
          </Button>
          <Button type="primary" onClick={() => navigate('/admin/bde-users/create')}>
            Create New BDE
          </Button>
        </div>
      </div>

      {!screens.md ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filteredUsers.map(user => {
            const total = orders
              .filter(o => o.bdeId === user.id && isEligibleForCommission(o.status))
              .reduce((sum, o) => sum + (Number(o.amount) * 0.10 || 0), 0);

            const paid = orders
              .filter(o => o.bdeId === user.id && o.commissionPaid && isEligibleForCommission(o.status))
              .reduce((sum, o) => sum + (Number(o.amount) * 0.10 || 0), 0);

            const remaining = orders
              .filter(o => o.bdeId === user.id && !o.commissionPaid && isEligibleForCommission(o.status))
              .reduce((sum, o) => sum + (Number(o.amount) * 0.10 || 0), 0);

            return (
              <Card
                key={user.id}
                title={user.name}
                extra={<Tag color={user.status === 'verified' ? 'green' : 'orange'}>{user.status.toUpperCase()}</Tag>}
                size="small"
                actions={[
                  <Button type="link" size="small" onClick={() => navigate(`/admin/bde-users/edit/${user.id}`)}>Edit</Button>,
                  <Button type="link" size="small" onClick={() => navigate(`/admin/bde-users/${user.id}/payouts`)}>History</Button>
                ]}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <p style={{ margin: 0 }}><strong>Mobile:</strong> {user.mobile}</p>
                  <p style={{ margin: 0 }}><strong>ID:</strong> {user.id}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', background: '#f8fafc', padding: 8, borderRadius: 4 }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Total Comm.</div>
                      <div style={{ fontWeight: 600 }}>₹{total.toFixed(2)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Paid</div>
                      <div style={{ fontWeight: 600, color: 'green' }}>₹{paid.toFixed(2)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Due</div>
                      <div style={{ fontWeight: 600, color: 'red' }}>₹{remaining.toFixed(2)}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    <Button
                      type="primary"
                      size="small"
                      disabled={remaining === 0}
                      onClick={() => handlePay(user.id)}
                    >
                      Pay Commission
                    </Button>
                    {user.status === 'pending' && (
                      <Button size="small" onClick={() => handleApprove(user)}>Approve</Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Table
          onRow={(record) => ({
            onDoubleClick: () => {
              navigate(`/admin/bde-users/${record.id}/payouts`);
            },
          })}
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          scroll={{ x: 'max-content' }}
        />
      )}
    </div>
  );
};

export default BDEList;
